import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { computed } from "vue";
import { JobRowProps, JobSchema, StepperStep } from "@/types";
import { jobStatus } from "@/util";
import {
  getVersionApiStore,
  jobApiStore,
  jobStore,
  logStore,
  permissionStore,
  useTimeDisplay,
  useVModel,
} from "@/hooks";
import {
  Typography,
  Icon,
  IconButton,
  TextButton,
  FlexBox,
  Stepper,
  Chip,
} from "@/components/common";


/**
 * Displays a row of the job table.
 */
const __default__ = {
  name: "JobRow",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    quasarProps: {},
    job: {},
    expanded: { type: Boolean }
  },
  emits: ["update:expanded", "view-logs"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const rowExpanded = useVModel(props, "expanded");

const currentStep = computed(() =>
  props.job.currentStep === -1 ? 1 : props.job.currentStep + 1
);
const steps = computed<StepperStep[]>(
  () =>
    props.job.steps?.map((title, idx) => ({
      title,
      done: idx < currentStep.value,
    })) || []
);

const { displayTime } = useTimeDisplay({
  getStart: () => props.job.startedAt,
  getEnd: () =>
    ["IN_PROGRESS", "COMPLETED"].includes(props.job.status)
      ? props.job.completedAt
      : props.job.lastUpdatedAt,
});

const displayLogs = computed(
  () => permissionStore.isSuperuser || process.env.NODE_ENV !== "production"
);

/**
 * Opens the view job expansion panel.
 */
function handleExpand(): void {
  jobStore.selectJob(props.job);
  rowExpanded.value = !rowExpanded.value;
}

/**
 * Attempts to delete a job.
 */
function handleDelete(): void {
  jobApiStore.handleDelete(props.job, {});
}

/**
 * Displays the log for a job.
 */
async function handleViewLogs(): Promise<void> {
  emit("view-logs", props.job);
}

/**
 * Navigates user to the completed project.
 */
function handleLoad(): void {
  if (props.job.completedEntityId) {
    getVersionApiStore.handleLoad(props.job.completedEntityId);
  } else {
    logStore.onError("Unable to view this project right now.");
  }
}

return (_ctx: any,_cache: any) => {
  const _component_q_td = _resolveComponent("q-td")!
  const _component_q_circular_progress = _resolveComponent("q-circular-progress")!
  const _component_q_tr = _resolveComponent("q-tr")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_q_tr, {
      props: props.quasarProps,
      class: "cursor-pointer",
      onClick: handleExpand
    }, {
      default: _withCtx(() => [
        _createVNode(_component_q_td, { class: "data-table-cell-300" }, {
          default: _withCtx(() => [
            _createVNode(_unref(IconButton), {
              tooltip: "Toggle expand",
              icon: props.expanded ? 'down' : 'up',
              class: "q-mr-md"
            }, null, 8, ["icon"]),
            _createVNode(_unref(Typography), {
              value: props.job.name
            }, null, 8, ["value"])
          ]),
          _: 1
        }),
        _createVNode(_component_q_td, { align: "end" }, {
          default: _withCtx(() => [
            _createVNode(_unref(Typography), {
              secondary: "",
              value: _unref(jobStatus)(props.job).progress(),
              "data-cy": "job-progress"
            }, null, 8, ["value"])
          ]),
          _: 1
        }),
        _createVNode(_component_q_td, { align: "end" }, {
          default: _withCtx(() => [
            _createVNode(_unref(Typography), {
              secondary: "",
              value: _unref(displayTime),
              "data-cy": "job-duration"
            }, null, 8, ["value"])
          ]),
          _: 1
        }),
        _createVNode(_component_q_td, { align: "end" }, {
          default: _withCtx(() => [
            _createVNode(_unref(Chip), {
              outlined: "",
              color: _unref(jobStatus)(props.job).color(),
              "data-cy": "job-status"
            }, {
              default: _withCtx(() => [
                (_unref(jobStatus)(props.job).isInProgress())
                  ? (_openBlock(), _createBlock(_component_q_circular_progress, {
                      key: 0,
                      color: _unref(jobStatus)(props.job).color(),
                      indeterminate: ""
                    }, null, 8, ["color"]))
                  : (_openBlock(), _createBlock(_unref(Icon), {
                      key: 1,
                      size: "sm",
                      color: _unref(jobStatus)(props.job).color(),
                      variant: _unref(jobStatus)(props.job).icon()
                    }, null, 8, ["color", "variant"])),
                _createVNode(_unref(Typography), {
                  l: "1",
                  value: _unref(jobStatus)(props.job).status()
                }, null, 8, ["value"])
              ]),
              _: 1
            }, 8, ["color"])
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["props"]),
    _withDirectives(_createVNode(_component_q_tr, {
      props: props.quasarProps
    }, {
      default: _withCtx(() => [
        _createVNode(_component_q_td, { colspan: "100%" }, {
          default: _withCtx(() => [
            _createVNode(_unref(Typography), {
              variant: "caption",
              value: props.job.name
            }, null, 8, ["value"]),
            _createVNode(_unref(Stepper), {
              minimal: "",
              "dense-labels": "",
              "hide-actions": "",
              "hide-step-back": "",
              "model-value": currentStep.value,
              steps: steps.value
            }, null, 8, ["model-value", "steps"]),
            _createVNode(_unref(FlexBox), {
              "full-width": "",
              justify: "end",
              b: "2"
            }, {
              default: _withCtx(() => [
                (displayLogs.value)
                  ? (_openBlock(), _createBlock(_unref(TextButton), {
                      key: 0,
                      outlined: "",
                      label: "View Logs",
                      r: "1",
                      "data-cy": "button-job-log",
                      icon: "logs",
                      onClick: handleViewLogs
                    }))
                  : _createCommentVNode("", true),
                _createVNode(_unref(TextButton), {
                  outlined: "",
                  label: "Delete Upload",
                  icon: "delete",
                  r: "1",
                  "data-cy": "button-delete-job",
                  onClick: handleDelete
                }),
                _createVNode(_unref(TextButton), {
                  label: "View Project",
                  color: "primary",
                  disabled: !_unref(jobStatus)(props.job).isCompleted(),
                  "data-cy": "button-open-job",
                  icon: "view-tree",
                  onClick: handleLoad
                }, null, 8, ["disabled"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["props"]), [
      [_vShow, props.expanded]
    ])
  ], 64))
}
}

})